// Admin → Swish IQ: the default teams a session runs selections for and the
// positions on each team. New sessions copy these; each session can change
// its own lists under Swish IQ → Sessions → Manage.

import React, { useEffect, useState } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export function ListEditor({ label, items, onChange, placeholder, disabled }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || items.some((i) => i.toLowerCase() === v.toLowerCase())) { setDraft(''); return; }
    onChange([...items, v]);
    setDraft('');
  };
  const move = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {items.map((item, idx) => (
          <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 999, padding: '3px 6px 3px 10px', fontWeight: 600, fontSize: '0.85rem' }}>
            {item}
            {!disabled && (
              <>
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move left" style={chipBtn}>‹</button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} title="Move right" style={chipBtn}>›</button>
                <button type="button" onClick={() => onChange(items.filter((_, i) => i !== idx))} title="Remove" style={{ ...chipBtn, color: '#c62828' }}>×</button>
              </>
            )}
          </span>
        ))}
        {items.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>None yet</span>}
      </div>
      {!disabled && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={draft}
            maxLength={30}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            style={{ padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, width: 200 }}
          />
          <button type="button" onClick={add} className="btn-cancel" style={{ padding: '6px 12px' }}>Add</button>
        </div>
      )}
    </div>
  );
}

const chipBtn = { border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '0 2px', color: '#555' };

export default function AdminSwishIq({ canEdit }) {
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    auditFetch(`${API_BASE}/swishiq-defaults`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || 'Could not load the SwishIQ Trials defaults');
        setTeams(d.teams || []);
        setPositions(d.positions || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const r = await auditFetch(`${API_BASE}/swishiq-defaults`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teams, positions }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setTeams(d.teams);
      setPositions(d.positions);
      setMessage('Saved. New SwishIQ Trials sessions will use these teams and positions.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-section">
      <h2 style={{ marginTop: 0 }}>SwishIQ Trials</h2>
      <p style={{ color: '#555', maxWidth: 720 }}>
        Default teams that trial sessions pick players for, and the positions on each team. New sessions start with these;
        each session can change its own under SwishIQ Trials → Sessions → Manage.
      </p>
      {loading ? <div className="loading">Loading...</div> : (
        <>
          <ListEditor label="Teams" items={teams} onChange={setTeams} placeholder="e.g. WSC" disabled={!canEdit} />
          <ListEditor label="Positions" items={positions} onChange={setPositions} placeholder="e.g. PG" disabled={!canEdit} />
          {canEdit && (
            <button type="button" className="btn-submit" onClick={save} disabled={saving || !teams.length || !positions.length}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </>
      )}
      {message && <p style={{ color: '#2e7d32' }}>{message}</p>}
      {error && <p style={{ color: '#c62828' }}>{error}</p>}
    </div>
  );
}
